import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import upsellModalImagePng from "~/assets/upsell-modal-image.png";
import { Button } from "@mindstonehq/ui";
import { X } from "lucide-react";
import { useNavigate } from "react-router";
import { useMindstonePlusUpsell } from "./mindstone-plus-upsell-context";
import metrics from "~/utils/metrics";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface ModalWrapperProps {
  isOpen: boolean;
  children: React.ReactNode;
  onClose: () => void;
}

const ModalWrapper: React.FC<ModalWrapperProps> = ({
  isOpen,
  children,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-transparent z-40"
            onClick={onClose}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ x: "calc(100% + 24px)", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "calc(100% + 24px)", opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
            }}
            className="fixed bottom-6 right-6 z-50 max-w-[420px] w-[calc(100%-48px)] sm:w-auto"
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export function MindstonePlusUpsellModal() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { isOpen, selectedCopy, activeTrigger, handleDismiss, handleCTAClick } =
    useMindstonePlusUpsell();

  const onCTAClick = () => {
    // Track CTA click
    metrics.track("mindstone_plus_upsell_cta_clicked", {
      trigger: activeTrigger,
      copy_variant: selectedCopy?.title,
    });

    // Navigate to programs page
    navigate("/programs");
    handleCTAClick();
  };

  const onDismiss = () => {
    // Track dismissal
    metrics.track("mindstone_plus_upsell_dismissed", {
      trigger: activeTrigger,
      copy_variant: selectedCopy?.title,
    });

    handleDismiss();
  };

  const onBackdropClick = () => {
    // Track backdrop click (implicit dismissal)
    metrics.track("mindstone_plus_upsell_backdrop_clicked", {
      trigger: activeTrigger,
      copy_variant: selectedCopy?.title,
    });

    handleDismiss();
  };

  // Track modal shown event
  React.useEffect(() => {
    if (isOpen && selectedCopy && activeTrigger) {
      metrics.track("mindstone_plus_upsell_shown", {
        trigger: activeTrigger,
        copy_variant: selectedCopy.title,
      });
    }
  }, [isOpen, selectedCopy, activeTrigger]);

  if (!selectedCopy) return null;

  return (
    <ModalWrapper isOpen={isOpen} onClose={onBackdropClick}>
      {/* Gradient border container */}
      <div className="bg-gradient-to-br from-blue-300 via-purple-300 to-purple-400 p-[3px] rounded-xl">
        {/* Main modal content */}
        <div className="bg-background rounded-lg p-6 relative">
          {/* Close button */}
          <button
            onClick={onDismiss}
            className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t(msg`Close`)}
          >
            <X size={20} />
          </button>

          {/* Modal content */}
          <div className="flex flex-col gap-5">
            {/* Image */}
            <div className="w-full">
              <img
                src={upsellModalImagePng}
                alt={t(msg`AI Program illustration`)}
                className="w-full h-auto rounded-lg border border-secondary"
              />
            </div>

            {/* Text content */}
            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-semibold text-foreground leading-tight">
                {t(selectedCopy.title)}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t(selectedCopy.description)}
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-4">
              <Button
                size="lg"
                onClick={onCTAClick}
                className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
              >
                {t(selectedCopy.cta)}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="w-full text-muted-foreground hover:text-foreground text-xs"
              >
                <Trans>Not now</Trans>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}
